import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Truck, User, Mail, Building, MapPin, ArrowLeft, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config/api';
import DriverTermsOfService from '../components/DriverTermsOfService';
import PasswordStrength from '../components/PasswordStrength';
import useRecaptcha from '../hooks/useRecaptcha';
import logger from '../utils/logger';

const DriverRegisterPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({ isValid: false, errors: [], strength: 0 });
  const navigate = useNavigate();
  const { register: registerUser, verifyPhone } = useAuth();
  const { executeRecaptcha, isReady, error: recaptchaError } = useRecaptcha();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    trigger
  } = useForm();

  const watchedPhone = watch('phone');

  // Register password as a controlled field once, with validation rules
  useEffect(() => {
    register('password', { 
      required: 'הסיסמה היא שדה חובה',
      minLength: {
        value: 10,
        message: 'הסיסמה חייבת להכיל לפחות 10 תווים'
      },
      maxLength: {
        value: 20,
        message: 'הסיסמה לא יכולה להכיל יותר מ-20 תווים'
      }
    });
  }, [register]);

  const sendVerificationCode = async () => {
    if (!watchedPhone) {
      toast.error('יש להזין מספר טלפון תחילה');
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.SMS}/send-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone: watchedPhone })
      });
      
      if (response.ok) {
        setShowVerification(true);
        toast.success('נשלח קוד אימות לטלפון שלך');
      } else {
        const data = await response.json();
        toast.error(data.message || 'שגיאה בשליחת קוד האימות');
      }
    } catch (error) {
      toast.error('שגיאה בשליחת קוד האימות');
    }
  };

  const verifyPhoneNumber = async () => {
    try {
      const result = await verifyPhone(watchedPhone, verificationCode);
      
      if (result.success) {
        setPhoneVerified(true);
        toast.success('מספר הטלפון אומת בהצלחה');
        setShowVerification(false);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('שגיאה באימות מספר הטלפון');
    }
  };

  const onSubmit = async (data) => {
    if (!phoneVerified) {
      toast.error('יש לאמת את מספר הטלפון תחילה');
      return;
    }

    if (!data.termsAccepted) {
      toast.error('יש לאשר את תנאי השימוש');
      return;
    }

    if (!passwordValidation.isValid) {
      toast.error('הסיסמה אינה עומדת בדרישות הבטיחות');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Execute reCAPTCHA before registration
      let recaptchaToken = null;
      if (isReady) {
        try {
          recaptchaToken = await executeRecaptcha('register_driver');
        } catch (error) {
          logger.error('reCAPTCHA execution failed:', error);
          toast.error('שגיאה באימות אבטחה. אנא נסה שוב.');
          setIsSubmitting(false);
          return;
        }
      } else if (recaptchaError) {
        logger.error('reCAPTCHA not ready:', recaptchaError);
        toast.error('שגיאה באימות אבטחה. אנא נסה שוב.');
        setIsSubmitting(false);
        return;
      }

      const result = await registerUser({
        ...data,
        serviceAreas: data.serviceAreas.split(',').map(area => area.trim()),
        vehicleCapacity: parseFloat(data.vehicleCapacity),
        recaptchaToken,
      });
      
      if (result.success) {
        toast.success('ההרשמה הושלמה בהצלחה! נשלחה הודעת אימות לטלפון שלך.');
        setStep(2);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      logger.error('Error during registration:', error);
      toast.error('שגיאה בהרשמה');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === 2) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50 flex items-center justify-center">
        <div className="card max-w-md w-full text-center hover-lift">
          <div className="text-success-600 mb-6">
            <Truck className="h-20 w-20 mx-auto" />
          </div>
          <h2 className="text-3xl font-bold text-brown-900 mb-6">
            ההרשמה הושלמה בהצלחה!
          </h2>
          <p className="text-brown-700 mb-8 text-lg">
            נשלחה הודעת אימות לטלפון שלך. אנא אמת את מספר הטלפון כדי להשלים את ההרשמה.
          </p>
          <div className="space-y-4">
            <button
              onClick={() => navigate('/driver/login')}
              className="btn-primary w-full text-lg py-4"
            >
              התחבר עכשיו
            </button>
            <button
              onClick={() => navigate('/')}
              className="btn-secondary w-full text-lg py-4"
            >
              חזור לדף הבית
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Skip link targets */}
        <div id="main-content" className="sr-only" tabIndex="-1">תוכן ראשי</div>
        {/* Header */}
        <div id="navigation" className="mb-10" tabIndex="-1">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-orange-600 hover:text-orange-700 mb-6 text-lg font-semibold transition-colors duration-300"
          >
            <ArrowLeft className="h-5 w-5 ml-3" />
            חזור לדף הבית
          </button>
          <h1 className="text-4xl font-bold text-brown-900 mb-4">הרשמת מובילים</h1>
          <p className="text-brown-700 text-xl">הצטרף למערכת וקבל בקשות הובלה ישירות לטלפון שלך</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-10">
          {/* Personal Information */}
          <div className="card hover-lift">
            <h2 className="text-2xl font-bold text-brown-900 mb-8 flex items-center">
              <div className="bg-orange-100 p-3 rounded-full ml-4">
                <User className="h-6 w-6 text-orange-600" />
              </div>
              פרטים אישיים
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label htmlFor="driver-name" className="form-label">שם מלא *</label>
                <input
                  id="driver-name"
                  type="text"
                  {...register('name', { required: 'השם הוא שדה חובה' })}
                  maxLength={100}
                  className="input-field"
                  placeholder="הזן את שמך המלא"
                />
                {errors.name && (
                  <p className="form-error">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="driver-phone" className="form-label">מספר טלפון *</label>
                <div className="flex space-x-3 space-x-reverse">
                  <input
                    id="driver-phone"
                    type="tel"
                    {...register('phone', { 
                      required: 'מספר הטלפון הוא שדה חובה',
                      pattern: {
                        value: /^05\d{8}$/,
                        message: 'מספר הטלפון חייב להיות בפורמט תקין (05XXXXXXXX)'
                      }
                    })}
                    maxLength={20}
                    className="input-field flex-1"
                    placeholder="05XXXXXXXX"
                  />
                  <button
                    type="button"
                    onClick={sendVerificationCode}
                    className="btn-secondary whitespace-nowrap px-6"
                  >
                    שלח קוד
                  </button>
                </div>
                {errors.phone && (
                  <p className="form-error">{errors.phone.message}</p>
                )}
                {phoneVerified && (
                  <p className="text-success-600 text-lg mt-2 font-semibold">✓ מספר הטלפון אומת</p>
                )}
              </div>
            </div>

            {/* Phone Verification */}
            {showVerification && (
              <div className="mt-6 p-6 bg-orange-50 rounded-2xl border-2 border-orange-200">
                <label htmlFor="verification-code" className="form-label">קוד אימות SMS</label>
                <div className="flex space-x-3 space-x-reverse">
                  <input
                    id="verification-code"
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder="הזן קוד אימות"
                    className="input-field flex-1 text-center text-2xl tracking-widest"
                    maxLength={6}
                  />
                  <button
                    type="button"
                    onClick={verifyPhoneNumber}
                    className="btn-primary whitespace-nowrap px-6"
                  >
                    אמת
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Account Information */}
          <div className="card hover-lift">
            <h2 className="text-2xl font-bold text-brown-900 mb-8 flex items-center">
              <div className="bg-orange-100 p-3 rounded-full ml-4">
                <Mail className="h-6 w-6 text-orange-600" />
              </div>
              פרטי חשבון
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label htmlFor="driver-email" className="form-label">כתובת אימייל *</label>
                <input
                  id="driver-email"
                  type="email"
                  {...register('email', { 
                    required: 'כתובת האימייל היא שדה חובה',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'כתובת האימייל אינה תקינה'
                    }
                  })}
                  maxLength={100}
                  className="input-field"
                  placeholder="הזן כתובת אימייל"
                />
                {errors.email && (
                  <p className="form-error">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="driver-password" className="form-label">סיסמה *</label>
                <PasswordStrength
                  id="driver-password"
                  value={watch('password') || ''}
                  onChange={(e) => {
                    setValue('password', e.target.value, { shouldValidate: true, shouldDirty: true });
                    // Ensure any dependent validation updates
                    trigger('password');
                  }}
                  onValidationChange={setPasswordValidation}
                  placeholder="הזן סיסמה חזקה"
                  error={errors.password?.message}
                />
              </div>
            </div>
          </div>

          {/* Company Information */}
          <div className="card hover-lift">
            <h2 className="text-2xl font-bold text-brown-900 mb-8 flex items-center">
              <div className="bg-orange-100 p-3 rounded-full ml-4">
                <Building className="h-6 w-6 text-orange-600" />
              </div>
              פרטי החברה
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label htmlFor="business-name" className="form-label">שם החברה *</label>
                <input
                  id="business-name"
                  type="text"
                  {...register('businessName', { required: 'שם החברה הוא שדה חובה' })}
                  maxLength={200}
                  className="input-field"
                  placeholder="הזן שם החברה"
                />
                {errors.businessName && (
                  <p className="form-error">{errors.businessName.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="contact-person" className="form-label">איש קשר *</label>
                <input
                  id="contact-person"
                  type="text"
                  {...register('contactPerson', { required: 'איש הקשר הוא שדה חובה' })}
                  maxLength={100}
                  className="input-field"
                  placeholder="הזן שם איש הקשר"
                />
                {errors.contactPerson && (
                  <p className="form-error">{errors.contactPerson.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="business-license" className="form-label">מספר רישיון עסק *</label>
                <input
                  id="business-license"
                  type="text"
                  {...register('businessLicense', { required: 'מספר רישיון עסק הוא שדה חובה' })}
                  maxLength={100}
                  className="input-field"
                  placeholder="הזן מספר רישיון עסק"
                />
                {errors.businessLicense && (
                  <p className="form-error">{errors.businessLicense.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="vehicle-type" className="form-label">סוג הרכב *</label>
                <select
                  id="vehicle-type"
                  {...register('vehicleType', { required: 'סוג הרכב הוא שדה חובה' })}
                  className="input-field"
                >
                  <option value="">בחר סוג רכב</option>
                  <option value="truck">משאית</option>
                  <option value="pickup">פיק אפ</option>
                </select>
                {errors.vehicleType && (
                  <p className="form-error">{errors.vehicleType.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="vehicle-capacity" className="form-label">קיבולת הרכב (טון) *</label>
                <input
                  id="vehicle-capacity"
                  type="number"
                  {...register('vehicleCapacity', { 
                    required: 'קיבולת הרכב היא שדה חובה',
                    min: {
                      value: 1,
                      message: 'קיבולת הרכב חייבת להיות לפחות 1 טון'
                    }
                  })}
                  min="1"
                  step="0.1"
                  className="input-field"
                  placeholder="הזן קיבולת ב טון"
                />
                {errors.vehicleCapacity && (
                  <p className="form-error">{errors.vehicleCapacity.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Service Areas */}
          <div className="card hover-lift">
            <h2 className="text-2xl font-bold text-brown-900 mb-8 flex items-center">
              <div className="bg-orange-100 p-3 rounded-full ml-4">
                <MapPin className="h-6 w-6 text-orange-600" />
              </div>
              אזורי שירות
            </h2>
            
            <div>
              <label htmlFor="service-areas" className="form-label">ערים שבהן אתה מספק שירות *</label>
              <input
                id="service-areas"
                type="text"
                {...register('serviceAreas', { required: 'אזורי השירות הם שדה חובה' })}
                maxLength={500}
                className="input-field"
                placeholder="הזן ערים מופרדות בפסיקים (לדוגמה: תל אביב, רמת גן, גבעתיים)"
              />
              {errors.serviceAreas && (
                <p className="form-error">{errors.serviceAreas.message}</p>
              )}
              <p className="text-sm text-brown-600 mt-2">
                הזן את הערים שבהן אתה מספק שירות, מופרדות בפסיקים
              </p>
            </div>
          </div>

          {/* Terms and Conditions */}
          <div className="card hover-lift">
            <h2 className="text-2xl font-bold text-brown-900 mb-8 flex items-center">
              <div className="bg-orange-100 p-3 rounded-full ml-4">
                <FileText className="h-6 w-6 text-orange-600" />
              </div>
              תנאי שימוש
            </h2>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-3 space-x-reverse">
                <input
                  type="checkbox"
                  id="termsAccepted"
                  {...register('termsAccepted', { 
                    required: 'יש לאשר את תנאי השימוש' 
                  })}
                  className="mt-1 h-5 w-5 text-orange-600 focus:ring-orange-500 border-brown-300 rounded"
                />
                <div className="flex-1">
                  <label htmlFor="termsAccepted" className="text-lg text-brown-700">
                    קראתי ואני מסכים ל{' '}
                    <button
                      type="button"
                      onClick={() => setShowTerms(true)}
                      className="text-orange-600 hover:text-orange-700 underline font-medium"
                    >
                      תנאי השימוש
                    </button>
                  </label>
                  {errors.termsAccepted && (
                    <p className="form-error mt-2">{errors.termsAccepted.message}</p>
                  )}
                </div>
              </div>
              
              <p className="text-lg text-brown-700">
                על ידי סימון תיבת הסימון, אתה מאשר שקראת והבנת את תנאי השימוש ומסכים להם.
                הסכמה זו מהווה תנאי חובה להרשמה למערכת.
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="text-center">
            <button
              type="submit"
              disabled={isSubmitting || !phoneVerified || !passwordValidation.isValid}
              className="btn-primary text-xl px-16 py-4 disabled:opacity-50 disabled:cursor-not-allowed hover-lift"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white ml-2"></div>
                  נרשם...
                </div>
              ) : (
                'השלם הרשמה'
              )}
            </button>
          </div>

          <div className="text-center">
            <p className="text-brown-600 text-lg">
              כבר יש לך חשבון?{' '}
              <Link
                to="/driver/login"
                className="text-orange-600 hover:text-orange-700 font-semibold underline"
              >
                התחבר עכשיו
              </Link>
            </p>
          </div>
        </form>
      </div>

      {/* Terms Modal */}
      {showTerms && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-brown-900">תנאי שימוש למובילים</h3>
              <button
                onClick={() => setShowTerms(false)}
                className="text-brown-500 hover:text-brown-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            <DriverTermsOfService />
            <div className="mt-6 text-center">
              <button
                onClick={() => setShowTerms(false)}
                className="btn-primary px-8 py-3"
              >
                הבנתי
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverRegisterPage;

